// src/components/subscription/SubscriptionBanner.js
import React, { useState } from 'react';
import { Crown, Sparkles, AlertCircle } from 'lucide-react';
import { useSubscription } from '../../Context/SubscriptionContext';
import FeatureLockModal from './FeatureLockModal';
import './SubscriptionBanner.css';

const SubscriptionBanner = () => {
  const { 
    isSubscribed, 
    isExpired,
    hasEverSubscribed,
    invoiceCount, 
    FREE_PLAN_LIMIT, 
    getRemainingInvoices,
    getSubscriptionStatus
  } = useSubscription();
  
  const [isModalOpen, setIsModalOpen] = useState(false);

  const status = getSubscriptionStatus();

  // Don't show banner if active premium
  if (status === 'active') return null;

  const remaining = getRemainingInvoices();
  
  // Expired subscription
  if (status === 'expired') {
    return (
      <>
        <div className="subscription-banner subscription-banner-expired">
          <div className="subscription-banner-content">
            <div className="subscription-banner-icon subscription-banner-icon-expired">
              <AlertCircle size={24} />
            </div>
            <div className="subscription-banner-text">
              <h3 className="subscription-banner-title">
                Subscription Expired
              </h3>
              <p className="subscription-banner-subtitle">
                Your premium features are locked. Renew your subscription to regain access.
              </p>
            </div>
            <button 
              className="subscription-banner-button subscription-banner-button-urgent"
              onClick={() => setIsModalOpen(true)}
            >
              <Sparkles size={16} />
              Renew Now
            </button>
          </div>
        </div>
        
        <FeatureLockModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          featureName="Premium Features"
          reason="Your subscription has expired. Renew to unlock all premium features."
        />
      </>
    );
  }

  // Had subscription before but inactive (cancelled)
  if (status === 'inactive') {
    return (
      <>
        <div className="subscription-banner subscription-banner-inactive">
          <div className="subscription-banner-content">
            <div className="subscription-banner-icon">
              <Crown size={24} />
            </div>
            <div className="subscription-banner-text">
              <h3 className="subscription-banner-title">
                Subscription Inactive
              </h3>
              <p className="subscription-banner-subtitle">
                Reactivate your subscription to access all premium features again.
              </p>
            </div>
            <button 
              className="subscription-banner-button"
              onClick={() => setIsModalOpen(true)}
            >
              <Sparkles size={16} />
              Reactivate
            </button>
          </div>
        </div>
        
        <FeatureLockModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          featureName="Premium Features"
          reason="Reactivate your subscription to unlock all premium features."
        />
      </>
    );
  }

  // Free user (never subscribed)
  const percentage = (invoiceCount / FREE_PLAN_LIMIT) * 100;

  return (
    <>
      <div className="subscription-banner">
        <div className="subscription-banner-content">
          <div className="subscription-banner-icon">
            <Crown size={24} />
          </div>
          <div className="subscription-banner-text">
            <h3 className="subscription-banner-title">
              Free Plan: {remaining} of {FREE_PLAN_LIMIT} invoices remaining
            </h3>
            <p className="subscription-banner-subtitle">
              Upgrade to unlock unlimited invoices and all features
            </p>
          </div>
          <button 
            className="subscription-banner-button"
            onClick={() => setIsModalOpen(true)}
          >
            <Sparkles size={16} />
            Upgrade Now
          </button>
        </div>
        <div className="subscription-progress-bar">
          <div 
            className="subscription-progress-fill" 
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      
      <FeatureLockModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        featureName="Premium Features"
        reason="Upgrade to access unlimited invoices and unlock all premium features for your business."
      />
    </>
  );
};

export default SubscriptionBanner;